import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Checkbox,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    ExpandMore as ExpandMoreIcon,
    Comment as CommentIcon,
} from '@mui/icons-material';
import { Program, ChecklistItem, User, ProgramProgressItem } from '@modules/programManager/types';
import { apiService } from '@modules/programManager/services/apiService';

const ProgramProgressView: React.FC = () => {
    const { programId } = useParams<{ programId: string }>();
    const navigate = useNavigate();

    const [program, setProgram] = useState<Program | null>(null);
    const [progress, setProgress] = useState<ProgramProgressItem[]>([]);

    // Group progress items by category
    const groupedProgress = progress.reduce((acc, item) => {
        const categoryTitle = item.categoryTitle || 'No Category';
        if (!acc[categoryTitle]) {
            acc[categoryTitle] = {
                categoryTitle,
                items: [],
                completedTasks: 0,
                totalTasks: 0
            };
        }
        acc[categoryTitle].items.push(item);
        acc[categoryTitle].totalTasks++;
        if (item.status === 'completed') {
            acc[categoryTitle].completedTasks++;
        }
        return acc;
    }, {} as Record<string, {
        categoryTitle: string;
        items: ProgramProgressItem[];
        completedTasks: number;
        totalTasks: number;
    }>);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editFormData, setEditFormData] = useState({
        status: 'pending' as 'pending' | 'in-progress' | 'completed' | 'not_applicable',
        assignedTo: '',
        dueDate: '',
        comments: '',
    });

    const fetchProgramData = useCallback(async () => {
        if (!programId) return;

        try {
            setLoading(true);
            setError(null);

            const [programData, progressData, usersData] = await Promise.all([
                apiService.getProgramById(programId),
                apiService.getProgramProgress(programId),
                apiService.getUsers(),
            ]);

            setProgram(programData);
            setProgress(progressData);
            setUsers(usersData);
        } catch (error) {
            console.error('Error fetching program data:', error);
            setError('Failed to fetch program data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [programId]);

    useEffect(() => {
        if (programId) {
            fetchProgramData();
        }
    }, [programId, fetchProgramData]);

    const handleEditItem = (item: ProgramProgressItem) => {
        setEditingItem(item as any); // Cast to ChecklistItem for compatibility
        setEditFormData({
            status: item.status as any, // Cast to expected status type
            assignedTo: item.assignedTo || '',
            dueDate: item.dueDate ? item.dueDate.split('T')[0] : '',
            comments: item.notes || '',
        });
        setEditDialogOpen(true);
    };

    const handleSaveItem = async () => {
        if (!editingItem || !programId) return;

        try {
            const updatedChecklistItem = await apiService.updateChecklistItem(programId, editingItem.id, editFormData);

            // Convert ChecklistItem to ProgramProgressItem format and update progress state
            setProgress(prevProgress =>
                prevProgress.map(item => {
                    if (item.id === editingItem.id) {
                        // Convert ChecklistItem to ProgramProgressItem
                        const updatedProgressItem: ProgramProgressItem = {
                            id: updatedChecklistItem.id,
                            programId: updatedChecklistItem.programId,
                            checklistId: updatedChecklistItem.id,
                            taskName: updatedChecklistItem.title,
                            status: updatedChecklistItem.status === 'completed' ? 'completed' :
                                updatedChecklistItem.status === 'in-progress' ? 'in-progress' : null,
                            assignedTo: updatedChecklistItem.assignedTo,
                            dueDate: updatedChecklistItem.dueDate,
                            completionDate: updatedChecklistItem.completedAt,
                            notes: updatedChecklistItem.comments,
                            categoryTitle: item.categoryTitle, // Preserve existing categoryTitle
                        };
                        return updatedProgressItem;
                    }
                    return item;
                })
            );

            setEditDialogOpen(false);
            setEditingItem(null);
        } catch (error) {
            console.error('Error updating checklist item:', error);
            setError('Failed to update task. Please try again.');
        }
    };


    // Handle task status change via checkbox
    const handleTaskStatusChange = async (task: ProgramProgressItem) => {
        try {
            let newStatus: 'pending' | 'in-progress' | 'completed' | null = null;
            let completionDate: string | null = null;

            if (task.status === null || task.status === 'pending') {
                newStatus = 'in-progress';
            } else if (task.status === 'in-progress') {
                newStatus = 'completed';
                completionDate = new Date().toISOString().split('T')[0]; // Today's date
            } else {
                // If already completed, cycle back to pending
                newStatus = 'pending';
                completionDate = null;
            }

            // Create specific activity message based on status change
            const statusMessages = {
                'in-progress': 'marked as in progress',
                'completed': 'marked as completed',
                'pending': 'marked as pending'
            };

            // Update task status via API
            await apiService.updateProgramProgress(
                task.programId,
                task.id,
                {
                    status: newStatus,
                    completionDate: completionDate,
                    // Add specific activity information
                    activityType: newStatus,
                    activityMessage: `Task "${task.taskName}" ${statusMessages[newStatus]} in program '${program?.title || 'Unknown'}'`
                }
            );

            // Refresh data
            fetchProgramData();
        } catch (error) {
            console.error('Error updating task status:', error);
            setError('Failed to update task status. Please try again.');
        }
    };

    const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return '-';
        try {
            return dayjs(dateString).format('DD/MM/YYYY');
        } catch (error) {
            console.error('Error formatting date:', error);
            return '-';
        }
    };

    const getUserName = (userId: string) => {
        const user = users.find(u => u.id === userId);
        return user ? user.name : 'Unknown User';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/program-manager')}
                >
                    Back to Programs
                </Button>
            </Box>
        );
    }

    if (!program) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Program not found.
                </Alert>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/program-manager')}
                >
                    Back to Programs
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ color: 'white', mb: 1 }}>
                        {program.title}
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {program.description}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/program-manager')}
                    sx={{
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                        },
                    }}
                >
                    Back to Programs
                </Button>
            </Box>

            {/* Program Stats */}
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3} sx={{ mb: 4 }}>
                <Box flex={{ xs: '1 1 100%', sm: '1' }} minWidth={{ xs: '100%', sm: '200px' }}>
                    <Card sx={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                                Duration
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                Due: {formatDate(program.dueDate)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box flex={{ xs: '1 1 100%', sm: '1' }} minWidth={{ xs: '100%', sm: '200px' }}>
                    <Card sx={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                                Assigned To
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {program.assignedTo ? getUserName(program.assignedTo) : 'Not Assigned'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Progress by Category */}
            <Typography variant="h5" sx={{ color: 'white', mb: 3 }}>
                Progress by Category
            </Typography>

            {Object.values(groupedProgress).map((categoryProgress) => (
                <Accordion key={categoryProgress.categoryTitle} sx={{ mb: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                        <Box display="flex" alignItems="center" width="100%">
                            <Typography variant="h6" sx={{ color: 'white', mr: 2 }}>
                                {categoryProgress.categoryTitle}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2} ml="auto">
                                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    {categoryProgress.completedTasks}/{categoryProgress.totalTasks} tasks completed
                                </Typography>
                            </Box>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Task</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Complete</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Assigned To</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Schedule Date</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Completion Date</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Comments</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categoryProgress.items.map((task) => (
                                        <TableRow key={task.id} hover>
                                            <TableCell sx={{ color: 'white' }}>
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                                                            opacity: task.status === 'completed' ? 0.7 : 1
                                                        }}
                                                    >
                                                        {task.taskName}
                                                    </Typography>
                                                    {task.notes && (
                                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                                            {task.notes}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    checked={task.status === 'completed' || task.status === 'in-progress'}
                                                    onChange={() => handleTaskStatusChange(task)}
                                                    color={
                                                        task.status === 'completed' ? 'success' :
                                                            task.status === 'in-progress' ? 'warning' :
                                                                'default'
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    color={
                                                        task.status === 'completed' ? 'success.main' :
                                                            task.status === 'in-progress' ? 'warning.main' :
                                                                'text.secondary'
                                                    }
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase',
                                                        fontSize: '0.7rem'
                                                    }}
                                                >
                                                    {task.status === 'in-progress' ? 'in progress' :
                                                        task.status === 'completed' ? 'completed' : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white' }}>
                                                {task.assignedTo ? (
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Avatar sx={{ width: 24, height: 24 }}>
                                                            <PersonIcon fontSize="small" />
                                                        </Avatar>
                                                        {getUserName(task.assignedTo)}
                                                    </Box>
                                                ) : (
                                                    'Not Assigned'
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: 'white' }}>
                                                {task.dueDate ? formatDate(task.dueDate) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ color: 'white' }}>
                                                {task.completionDate ? formatDate(task.completionDate) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ color: 'white' }}>
                                                {task.notes ? (
                                                    <Tooltip title={task.notes}>
                                                        <IconButton size="small">
                                                            <CommentIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditItem(task)}
                                                    sx={{ color: '#2196F3' }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Edit Task Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Task</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {editingItem?.title}
                            </Typography>
                            {editingItem?.description && (
                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                    {editingItem.description}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={editFormData.status}
                                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value as any })}
                                    label="Status"
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="in-progress">In Progress</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                    <MenuItem value="not_applicable">Not Applicable</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel>Assigned To</InputLabel>
                                <Select
                                    value={editFormData.assignedTo}
                                    onChange={(e) => setEditFormData({ ...editFormData, assignedTo: e.target.value })}
                                    label="Assigned To"
                                >
                                    <MenuItem value="">Not Assigned</MenuItem>
                                    {users.map((user) => (
                                        <MenuItem key={user.id} value={user.id}>
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Schedule Date"
                                type="date"
                                value={editFormData.dueDate}
                                onChange={(e) => setEditFormData({ ...editFormData, dueDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                                        filter: 'invert(1)',
                                        cursor: 'pointer'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'white',
                                        },
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Comments"
                                multiline
                                rows={3}
                                value={editFormData.comments}
                                onChange={(e) => setEditFormData({ ...editFormData, comments: e.target.value })}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'white',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveItem} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProgramProgressView;
export { };
